import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ApiClient from '../methods/api/apiClient';
import datepipeModel from '../models/datepipemodel';
import { encryptId } from '../components/common/Encryption/encryption';

const RsvpConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);

  const eventId = searchParams.get('eventId');
  const status = searchParams.get('status');
  const error = searchParams.get('error');
  const attendeeEmail = searchParams.get('email');
  // cw: guests can't log in, so offer a login-free "request to be grouped" link using their rsvpToken.
  const rsvpToken = searchParams.get('token');
  const attendeeType = searchParams.get('type');

  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState<any>(null);
  // cw: calendar download (shown after an RSVP Yes). Fetched from the public api/event/ics.
  const [cal, setCal] = useState<{ ics: string; gcalUrl: string; event: any } | null>(null);

  useEffect(() => {
    if (error) {
      setLoading(false);
      return;
    }
    if (!eventId || !status) {
      setLoading(false);
      return;
    }
    // If logged in as a different user than the RSVP recipient, go straight to event page
    if (user?._id && attendeeEmail && user.email?.toLowerCase() !== attendeeEmail.toLowerCase()) {
      encryptId(String(eventId)).then(encrypted => navigate(`/event/detail/${encrypted}`, { replace: true }));
      return;
    }
    if (user?._id) {
      loadEventDetails(eventId);
    } else {
      setLoading(false);
    }
  }, [eventId, status, error]);

  const loadEventDetails = async (eid: string) => {
    try {
      const eventRes = await ApiClient.get('api/event/details', { id: eid });
      if (eventRes.success) setEventData(eventRes.data);
    } catch (err: any) {
      // Event details may fail for non-logged-in users
    } finally {
      setLoading(false);
    }
  };

  // cw: after an RSVP Yes, load the calendar event (public endpoint — works logged out too).
  useEffect(() => {
    if (error || status !== 'Yes' || !eventId) return;
    ApiClient.get('api/event/ics', { eventId }).then((res: any) => {
      if (res.success && res.ics) setCal({ ics: res.ics, gcalUrl: res.gcalUrl, event: res.event });
    }).catch(() => { /* non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, status, error]);

  const downloadIcs = () => {
    if (!cal?.ics) return;
    const blob = new Blob([cal.ics], { type: 'text/calendar;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(cal.event?.title || 'event').replace(/[^a-z0-9]+/gi, '_')}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-4">This RSVP link is not valid or has expired.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Confirming your RSVP...</div>;
  }

  if (!eventId || !status) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-4">This RSVP link is not valid.</p>
        </div>
      </div>
    );
  }

  const handleCheckEventDetails = async () => {
    const encryptedId = await encryptId(String(eventId));
    if (user?._id) {
      navigate(`/event/detail/${encryptedId}`);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(`/event/detail/${encryptedId}`)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
        <div className="text-green-500 text-6xl mb-4">&#10003;</div>

        <h1 className="text-2xl font-bold mb-2">
          {status === 'Yes' ? 'RSVP Confirmed — You\'re attending!' : 'RSVP Confirmed — You\'re not attending.'}
        </h1>

        {eventData && (
          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              {status === 'Yes'
                ? `You've confirmed your attendance for:`
                : `You've indicated you cannot attend:`}
            </p>
            <p className="font-semibold text-lg">{eventData.title}</p>
            <p className="text-gray-500 text-sm mt-2">
              {datepipeModel.formatDateToTimezone(eventData.date, eventData.timezone)}
            </p>
            <p className="text-gray-500 text-sm">{eventData.address}</p>
          </div>
        )}

        {status === 'Yes' && cal && (
          <div className="mb-6 border border-orange-200 bg-orange-50 rounded-lg p-4 text-left">
            <p className="font-semibold text-gray-800 mb-1 text-center">Add the event to your calendar:</p>
            {cal.event?.joinLink && (
              <p className="text-sm text-gray-600 mb-2 text-center">
                Virtual meeting — Zoom link:{' '}
                <a href={cal.event.joinLink} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{cal.event.joinLink}</a>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button onClick={downloadIcs} className="flex-1 bg-orange-500 text-white px-4 py-2.5 rounded hover:bg-orange-600 text-sm font-medium">
                Download calendar event on the app (.ics)
              </button>
              <a href={cal.gcalUrl} target="_blank" rel="noreferrer" className="flex-1 border border-orange-300 text-orange-700 px-4 py-2.5 rounded hover:bg-orange-100 text-sm font-medium text-center">
                Add to Google Calendar on the website
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">The invite shows the correct time in your local timezone.</p>
          </div>
        )}

        {/* cw: guests (no login) can request to be grouped for a meet-up via their rsvpToken. */}
        {status === 'Yes' && attendeeType === 'guest' && rsvpToken && (
          <div className="mb-6 border border-blue-200 bg-blue-50 rounded-lg p-4">
            <p className="font-semibold text-gray-800 mb-1 text-center">Want to meet people at this event?</p>
            <p className="text-sm text-gray-600 mb-3 text-center">Ask the group leader to place you in a small group.</p>
            <a
              href={`/cm-request?token=${encodeURIComponent(rsvpToken)}`}
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded text-center"
            >
              Request to be grouped for a meet-up
            </a>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={handleCheckEventDetails}
            className="bg-orange-500 text-white px-6 py-3 rounded hover:bg-orange-600 w-full"
          >
            Check Event Details
          </button>
          {user?._id && (
            <button
              onClick={() => navigate('/')}
              className="border border-gray-300 text-gray-700 px-6 py-3 rounded hover:bg-gray-50 w-full"
            >
              Back to Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RsvpConfirmation;
